import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Wallet, CreditCard, Loader2, CheckCircle2, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { api, errMsg, eur, fmtDateTime } from '../api';
import { usePortal } from '../PortalAuth';
import { PageHeader, Card, SectionTitle, inputCls, goldBtn } from '../ui';
import WalletAlertCard from './WalletAlertCard';
import StatementsCard from './StatementsCard';
import SubscriptionsCard from './SubscriptionsCard';

function usePollTopup(sessionId, onPaid) {
  const [state, setState] = useState(sessionId ? 'checking' : null);
  useEffect(() => {
    if (!sessionId) return undefined;
    let n = 0; let stop = false;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/wallet/topup/status/${sessionId}`);
        if (data.status === 'paid') { setState('paid'); onPaid(data); return; }
        if (data.status === 'expired') { setState('expired'); return; }
      } catch { setState('error'); return; }
      if (++n < 10) setTimeout(tick, 2000); else setState('timeout');
    };
    tick();
    return () => { stop = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function MemberTopup({ user }) {
  const [amount, setAmount] = useState(100);
  const [note, setNote] = useState('');
  const [mine, setMine] = useState([]);
  const load = () => api.get('/topup-requests').then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => { try { await api.post('/topup-requests', { amount_eur: Number(amount), note }); toast.success('Richiesta inviata al titolare'); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!user.permissions?.topup) return <><SectionTitle>Ricarica</SectionTitle><p className="text-sm text-[#8A8F97]" data-testid="member-topup-disabled">Le ricariche del plafond sono gestite dal titolare dell'account.</p></>;
  return (
    <div data-testid="member-topup">
      <SectionTitle>Richiedi una ricarica</SectionTitle>
      <p className="text-sm text-[#8A8F97] mb-4">La richiesta arriva al titolare dell'account, che la approva e la paga.</p>
      <Input type="number" min={20} max={5000} value={amount} onChange={(e) => setAmount(e.target.value)} className={`${inputCls} mb-3 font-mono`} data-testid="member-topup-amount" />
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motivo (facoltativo)" className={`${inputCls} mb-4`} data-testid="member-topup-note" />
      <Button onClick={send} disabled={amount < 20 || amount > 5000} className={`${goldBtn} h-12 px-6`} data-testid="member-topup-send">Invia richiesta</Button>
      {mine.map((r) => <p key={r.id} className="text-xs text-[#8A8F97] mt-3" data-testid={`member-topup-row-${r.id}`}>{eur(r.amount_cents)} · {r.status === 'pending' ? 'in attesa' : r.status === 'approved' ? 'approvata' : 'rifiutata'}</p>)}
    </div>
  );
}

export default function WalletPage() {
  const { user, refresh } = usePortal();
  const [params, setParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [packs, setPacks] = useState({ packs: [50, 100, 250, 500], min: 20, max: 5000 });
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/wallet').then((r) => setWallet(r.data)).catch(() => {});

  const status = usePollTopup(params.get('session_id'), (d) => { toast.success(`Ricarica di ${eur(d.amount_cents)} completata`); refresh(); load(); });

  useEffect(() => {
    load();
    api.get('/wallet/packs').then((r) => setPacks(r.data)).catch(() => {});
    if (params.get('cancelled')) { toast.info('Pagamento annullato'); setParams({}); }
    if (Number(params.get('ricarica')) >= 20) setAmount(Number(params.get('ricarica')));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (method) => {
    setBusy(true);
    try {
      const { data } = await api.post('/wallet/topup', { amount_eur: Number(amount), origin_url: window.location.origin, method });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div>
      <PageHeader kicker="Plafond prepagato in euro" title="Plafond" />
      {status && status !== 'paid' && (
        <Card className="p-4 mb-6 flex items-center gap-3 text-sm" data-testid="topup-status">
          {status === 'checking' ? <><Loader2 className="animate-spin text-[#D4AF37]" size={16} /> Verifica del pagamento in corso…</> : <span className="text-[#E37A72]">Pagamento non confermato ({status}). Se è stato addebitato, il credito arriverà a breve.</span>}
        </Card>
      )}
      {status === 'paid' && <Card className="p-4 mb-6 flex items-center gap-3 text-sm text-[#5FC18F]" data-testid="topup-success"><CheckCircle2 size={16} /> Ricarica completata, il plafond è aggiornato.</Card>}

      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-4 mb-8">
        <Card className="p-8 relative overflow-hidden">
          <Wallet className="absolute -right-6 -bottom-6 text-[#D4AF37]/[0.06]" size={180} />
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A7F87] mb-4">Saldo disponibile</p>
          <p className="font-mono text-5xl text-[#F2F2F2] mb-3" data-testid="wallet-balance">{eur(user.wallet_cents)}</p>
          <p className="text-sm text-[#7A7F87] max-w-xs">Ogni report, documento o attivazione di monitoraggio scala il prezzo indicato prima dell'acquisto.</p>
        </Card>
        <Card className="p-8">
          {user.is_owner === false ? <MemberTopup user={user} /> : <>
          <SectionTitle>Ricarica</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {packs.packs.map((p) => (
              <button key={p} onClick={() => setAmount(p)} data-testid={`topup-pack-${p}`} className={`h-14 border rounded-sm font-mono transition-colors ${Number(amount) === p ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#1F242C] text-[#C9CCD1] hover:border-[#3A3F47]'}`}>{p} €</button>
            ))}
          </div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#7A7F87] mb-2">Importo personalizzato (€{packs.min}–{packs.max})</label>
          <Input type="number" min={packs.min} max={packs.max} value={amount} onChange={(e) => setAmount(e.target.value)} className={`${inputCls} mb-6 font-mono`} data-testid="topup-amount" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Button onClick={() => pay('stripe')} disabled={busy || amount < packs.min || amount > packs.max} className={`${goldBtn} h-12`} data-testid="topup-stripe">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <><CreditCard size={16} className="mr-2" />Paga con carta</>}
            </Button>
            <Button disabled={!packs.paypal_enabled} className="h-12 rounded-sm bg-[#0B0D10] border border-[#1F242C] text-[#5A5F67]" data-testid="topup-paypal" title="In attivazione">PayPal · in arrivo</Button>
          </div>
          </>}
        </Card>
      </div>

      <SubscriptionsCard />
      {user.is_owner !== false && <WalletAlertCard />}
      <StatementsCard />

      <Card>
        <div className="px-6 pt-6"><SectionTitle>Movimenti</SectionTitle></div>
        {!wallet || wallet.ledger.length === 0 ? <p className="px-6 pb-6 text-sm text-[#5A5F67]">Nessun movimento.</p> : (
          <div className="divide-y divide-[#1F242C]" data-testid="wallet-ledger">
            {wallet.ledger.map((l) => (
              <div key={l.id} className="px-6 py-4 flex items-center gap-4">
                {l.type === 'credit' ? <ArrowDownLeft size={16} className="text-[#5FC18F]" /> : <ArrowUpRight size={16} className="text-[#E37A72]" />}
                <div className="flex-1 min-w-0"><p className="text-sm truncate">{l.reason}</p><p className="text-xs text-[#5A5F67] font-mono">{fmtDateTime(l.created_at)}</p></div>
                <span className={`font-mono text-sm ${l.type === 'credit' ? 'text-[#5FC18F]' : 'text-[#E5E5E5]'}`}>{l.type === 'credit' ? '+' : '−'}{eur(l.amount_cents)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
